import React, { useEffect, useState, useCallback } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Box, LinearProgress, Skeleton, Stack, TableBody, TableCell, TableHead, TablePagination,
  TableRow, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  DataTable, DatagridEmpty, ListScreen, PageTitle, RecordCardList, SectionHeader,
} from '@/components/shell';
import type { IOcrTestRun } from '@/types';
import { ocrTestingService } from './ocrTestingService';
import { formatDuration, formatRunDate, passRate, passRateColor } from './runFormat';
import { RunState } from './RunState';

const PAGE_SIZE = 25;
const COLUMNS = 9;

/** A failure or error count reads in the status ink only when it is not zero. */
const Count: React.FC<{ value: number; tone: 'error' | 'warning' }> = ({ value, tone }) => (
  <Box component="span" sx={{ color: value > 0 ? `${tone}.dark` : 'text.primary' }}>{value}</Box>
);

const PassRate: React.FC<{ run: IOcrTestRun }> = ({ run }) => {
  const theme = useTheme();
  const rate = passRate(run);
  const isRunning = !run.finishedAt;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={isRunning ? 0 : rate}
        color={passRateColor(rate)}
        aria-hidden
        sx={{ flex: 1, height: 6, borderRadius: theme.custom.radius.full }}
      />
      <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {isRunning ? '—' : `${rate}%`}
      </Typography>
    </Box>
  );
};

export const TestRunsList: React.FC = () => {
  const t = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [runs, setRuns] = useState<IOcrTestRun[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ocrTestingService.listRuns(page + 1, PAGE_SIZE);
      setRuns(data.data);
      setTotal(data.recordsTotal);
    } catch {
      notify('screens.ocrTesting.loadRunsFailed', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, notify]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const openRun = (run: IOcrTestRun) => navigate(`/ocr-testing/runs/${run.id}`);

  const header = (
    <SectionHeader
      left={<PageTitle title={t('screens.ocrTesting.runsTitle')} subtitle={t('screens.ocrTesting.runsSubtitle')} />}
      onRefresh={fetchRuns}
      refreshing={loading}
    />
  );

  const empty = <DatagridEmpty title={t('screens.ocrTesting.noRuns')} hint={t('screens.ocrTesting.noRunsHint')} />;

  const pagination = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={PAGE_SIZE}
      rowsPerPageOptions={[]}
    />
  );

  if (isPhone) {
    return (
      <ListScreen header={header}>
        {loading ? (
          <Stack spacing={1}>{[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={96} />)}</Stack>
        ) : (
          <>
            <RecordCardList
              records={runs}
              getKey={(run) => run.id}
              empty={empty}
              mobile={{
                primary: (run) => formatRunDate(run.startedAt),
                status: (run) => (run.finishedAt ? undefined : <RunState run={run} />),
                lines: [
                  {
                    label: t('screens.ocrTesting.passRate'),
                    render: (run) => (run.finishedAt
                      ? t('screens.ocrTesting.passedOf', { rate: passRate(run), passed: run.passedCases, total: run.totalCases })
                      : '—'),
                  },
                  {
                    label: t('screens.ocrTesting.failedErrors'),
                    render: (run) => <><Count value={run.failedCases} tone="error" /> / <Count value={run.errorCases} tone="warning" /></>,
                  },
                  { label: t('screens.ocrTesting.duration'), render: (run) => formatDuration(run.startedAt, run.finishedAt) },
                  { label: t('screens.ocrTesting.sdkVersion'), render: (run) => run.sdkVersion || '—' },
                ],
                onClick: openRun,
              }}
            />
            {total > PAGE_SIZE && pagination}
          </>
        )}
      </ListScreen>
    );
  }

  return (
    <ListScreen header={header}>
      <DataTable
        fill
        label={t('screens.ocrTesting.runsTitle')}
        empty={!loading && runs.length === 0 ? empty : undefined}
        footer={total > PAGE_SIZE ? pagination : undefined}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('screens.ocrTesting.date')}</TableCell>
            <TableCell>{t('screens.ocrTesting.sdkVersion')}</TableCell>
            <TableCell align="right">{t('screens.ocrTesting.total')}</TableCell>
            <TableCell align="right">{t('screens.ocrTesting.passed')}</TableCell>
            <TableCell align="right">{t('screens.ocrTesting.failed')}</TableCell>
            <TableCell align="right">{t('screens.ocrTesting.errors')}</TableCell>
            <TableCell sx={{ minWidth: 160 }}>{t('screens.ocrTesting.passRate')}</TableCell>
            <TableCell>{t('screens.ocrTesting.duration')}</TableCell>
            <TableCell>{t('screens.ocrTesting.status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? [...Array(6)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(COLUMNS)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
              </TableRow>
            ))
            : runs.map((run) => (
              <TableRow key={run.id} onClick={() => openRun(run)} sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatRunDate(run.startedAt)}</TableCell>
                <TableCell>{run.sdkVersion || '—'}</TableCell>
                <TableCell align="right">{run.totalCases}</TableCell>
                <TableCell align="right">{run.passedCases}</TableCell>
                <TableCell align="right"><Count value={run.failedCases} tone="error" /></TableCell>
                <TableCell align="right"><Count value={run.errorCases} tone="warning" /></TableCell>
                <TableCell><PassRate run={run} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDuration(run.startedAt, run.finishedAt)}</TableCell>
                <TableCell><RunState run={run} /></TableCell>
              </TableRow>
            ))}
        </TableBody>
      </DataTable>
    </ListScreen>
  );
};
